import { useState } from "react";
import { HelpCircle, Send, Users } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  useAskQuestion,
  useMyQuestions,
  usePublicQA,
} from "@/features/rfq/api/proceedings";

/**
 * Pytania oferenta — jego własny, tajny wątek z GK.
 *
 * Oferent widzi wyłącznie SWOJE pytania i odpowiedzi; cudzych nie ma prawa zobaczyć.
 * Pytania przyjmujemy tylko do terminu składania.
 */

export function QuestionsOfferer({
  proceedingId,
  open,
}: {
  proceedingId: string;
  open: boolean;
}) {
  const { data } = useMyQuestions(proceedingId);
  const { data: publicQa } = usePublicQA(proceedingId);
  const ask = useAskQuestion(proceedingId);
  const [body, setBody] = useState("");
  const [error, setError] = useState("");

  const submit = () => {
    if (!body.trim()) return setError("Wpisz treść pytania.");
    setError("");
    ask.mutate(body.trim(), { onSuccess: () => setBody("") });
  };

  const list = data ?? [];

  const publicList = publicQa ?? [];

  return (
    <div className="bg-white p-4 dark:bg-gray-800">
      {publicList.length > 0 && (
        <div className="mb-4 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
          <p className="mb-2 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
            <Users size={12} /> Pytania i odpowiedzi udostępnione wszystkim
          </p>
          <ul className="space-y-3">
            {publicList.map((qa) => (
              <li key={qa.id} className="border-l-2 border-[var(--color-border)] pl-3">
                <p className="flex items-start gap-1.5 text-sm text-[var(--color-text-primary)]">
                  <HelpCircle size={13} className="mt-0.5 shrink-0 text-[var(--color-text-muted)]" />
                  {qa.body}
                </p>
                <p className="mt-1 whitespace-pre-wrap border-l-2 border-[var(--color-primary)] pl-2 text-sm text-[var(--color-text-secondary)]">
                  {qa.answer}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {list.length > 0 && (
        <ul className="mb-4 space-y-3">
          {list.map((q) => (
            <li key={q.id} className="border-l-2 border-[var(--color-border)] pl-3">
              <p className="flex items-start gap-1.5 text-sm text-[var(--color-text-primary)]">
                <HelpCircle size={13} className="mt-0.5 shrink-0 text-[var(--color-text-muted)]" />
                {q.body}
              </p>
              {q.answer ? (
                <p className="mt-1 whitespace-pre-wrap border-l-2 border-[var(--color-primary)] pl-2 text-sm text-[var(--color-text-secondary)]">
                  {q.answer}
                </p>
              ) : (
                <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                  Oczekuje na odpowiedź.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {open ? (
        <div className="space-y-2">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
            placeholder="Zadaj pytanie do postępowania — trafia wyłącznie do GK ELEKTIM."
            className="w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
          />
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-[var(--color-text-muted)]">
              Pytanie jest poufne — inni oferenci go nie zobaczą.
            </p>
            <Button onClick={submit} disabled={ask.isPending}>
              <Send size={14} className="mr-1.5 inline" /> Wyślij pytanie
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-[var(--color-text-muted)]">
          Termin minął — pytań już nie przyjmujemy.
        </p>
      )}
    </div>
  );
}
